import { Button } from "@trotroways/ui";
import { cn, formatTime } from "@trotroways/utils";
import {
  ArrowLeftRight,
  Bus,
  CarTaxiFront,
  ChevronLeft,
  Clock,
  Footprints,
  Loader2,
} from "lucide-react";
import { memo, useCallback, useState } from "react";
import type { Location, RouteOption } from "@/lib/api";
import type { SavedRoute } from "@/lib/saved-routes";

export interface RouteOptionsPanelProps {
  routes: RouteOption[];
  origin: Location | null;
  destination: Location | null;
  isLoading: boolean;
  loadError?: string | null;
  onRetry?: () => void;
  onMissingReport?: () => void;
  recentSuggestions?: SavedRoute[];
  onSelectSuggestion?: (recent: SavedRoute) => void;
  onSelectRoute: (route: RouteOption) => void;
  onBack: () => void;
}

const ROUTE_LABELS: Record<string, string> = {
  fastest: "Fastest",
  cheapest: "Cheapest",
  shortest: "Fewest Transfers",
  recommended: "Recommended",
};

export const RouteOptionsPanel = memo(function RouteOptionsPanel({
  routes,
  origin,
  destination,
  isLoading,
  loadError,
  onRetry,
  onMissingReport,
  recentSuggestions,
  onSelectSuggestion,
  onSelectRoute,
  onBack,
}: RouteOptionsPanelProps) {
  const [missingReported, setMissingReported] = useState(false);

  const handleRouteClick = useCallback(
    (route: RouteOption) => () => onSelectRoute(route),
    [onSelectRoute],
  );

  const handleReportMissing = useCallback(() => {
    onMissingReport?.();
    setMissingReported(true);
  }, [onMissingReport]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="size-8 -ml-2 rounded-full"
          onClick={onBack}
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <h3 className="font-semibold">Route Options</h3>
      </div>

      <div className="flex items-start gap-3 py-2">
        <div className="flex-1 min-w-0 space-y-2">
          <p className="text-base font-medium truncate">
            To {destination?.name || "Destination"}
          </p>
          <p className="text-sm font-medium truncate">
            <span className="text-muted-foreground">From</span> {origin?.name || "Current Location"}
          </p>
        </div>
      </div>

      <div className="w-full border border-muted -mx-2 " />

      <div className="space-y-3">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground mt-2">
              Finding best routes...
            </p>
          </div>
        ) : loadError ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-sm font-medium">Something went wrong</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-60">
              We couldn't complete your search. Check your connection and try again.
            </p>
            {onRetry && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={onRetry}
              >
                Try again
              </Button>
            )}
          </div>
        ) : routes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-sm font-medium">No routes found</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-60">
              We don't cover this journey yet. Try nearby major stops, or let us know.
            </p>
            {missingReported ? (
              <p className="mt-4 text-xs text-green-400">
                Thanks — we'll look into adding this route.
              </p>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={handleReportMissing}
              >
                Report missing route
              </Button>
            )}
            {recentSuggestions && recentSuggestions.length > 0 && onSelectSuggestion && (
              <div className="mt-5 space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400">
                  Your recent searches
                </p>
                <div className="flex flex-wrap justify-center gap-1.5">
                  {recentSuggestions.slice(0, 3).map((recent) => (
                    <button
                      key={recent.id}
                      type="button"
                      onClick={() => onSelectSuggestion(recent)}
                      className="max-w-full truncate rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-white/80 transition-colors hover:bg-white/10"
                    >
                      {recent.fromName} → {recent.toName}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          routes.map((route) => {
            const confidence = route.confidence ?? 0;
            const relBadge =
              confidence >= 0.7
                ? "High reliability"
                : confidence >= 0.4
                  ? "Medium reliability"
                  : confidence > 0
                    ? "Low reliability"
                    : null;
            const relColor =
              confidence >= 0.7
                ? "text-green-400"
                : confidence >= 0.4
                  ? "text-amber-400"
                  : confidence > 0
                    ? "text-red-400"
                    : "";
            const label = ROUTE_LABELS[route.type] || route.title;

            return (
              <button
                type="button"
                key={route.id}
                onClick={handleRouteClick(route)}
                className="w-full text-left p-4 rounded-lg border border-white/20 bg-[#0004] transition-colors"
              >
                <div className="flex items-center justify-between pb-3 mb-3 border-b border-white/10">
                  <span className="font-medium text-sm">{label}</span>
                  <span className="text-sm font-semibold text-white/80">
                    ₵{route.totalPrice.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-white">
                  <span className="flex items-center gap-1">
                    <Clock className="size-3" />
                    {route.totalP10 != null && route.totalP90 != null
                      ? `${formatTime(route.totalP10)}–${formatTime(route.totalP90)}`
                      : formatTime(route.totalTime)}
                  </span>
                  <span className="flex items-center gap-1">
                    <ArrowLeftRight className="w-3 h-3" />
                    {route.transfers} {route.transfers === 1 ? "transfer" : "transfers"}
                  </span>
                </div>
                {relBadge && (
                  <div className={`mt-2 text-[11px] font-medium ${relColor}`}>
                    {relBadge}
                  </div>
                )}
                {route.headwayMinSecs != null &&
                  route.headwayMaxSecs != null && (
                    <div className="mt-1.5 text-[11px] text-white/50">
                      Wait: {formatTime(route.headwayMinSecs)}–
                      {formatTime(route.headwayMaxSecs)}
                    </div>
                  )}
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  {route.steps.map((step, index) => {
                    const isWalking = step.type === "walking";
                    const isInformal =
                      step.type !== "walking" && step.scheduled === false;
                    const lineColor = step.color || "#2EC4B6";
                    const StepIcon = isWalking
                      ? Footprints
                      : isInformal
                        ? CarTaxiFront
                        : Bus;

                    return (
                      <span key={step.id} className="flex items-center gap-1.5">
                        <span
                          className={cn(
                            "flex items-center justify-center",
                            isInformal &&
                            "rounded-full p-0.5",
                          )}
                          style={isInformal ? { borderColor: `${lineColor}80` } : undefined}
                        >
                          <StepIcon
                            className="size-3.5"
                            style={{
                              color: isWalking ? undefined : lineColor,
                              opacity: isWalking ? 0.8 : undefined,
                            }}
                          />
                        </span>
                        {index < route.steps.length - 1 && (
                          <span className="text-white/40 text-xs mx-0.5">→</span>
                        )}
                      </span>
                    );
                  })}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
});
